import React, { useMemo, useEffect } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { SpotRequest, SpotRequestFilters, MergeSpotRequest, SpotAddRequest, getMergeRequestDistance } from "@/common/model/SpotRequest";
import RequestCard from "./RequestCard";
import RequestFilters from "./RequestFilters";

interface RequestListPanelProps {
  requests: SpotRequest[];
  selectedRequestId: number | null;
  selectedRequestType: string | null;
  hoveredRequestId: number | null;
  hoveredRequestType: string | null;
  onRequestSelect: (request: SpotRequest) => void;
  onRequestHover: (request: SpotRequest | null) => void;
  onReview: (request: SpotRequest) => void;
  onUserClick?: (userId: number, username: string) => void;
  filters: SpotRequestFilters;
  onFiltersChange: (filters: SpotRequestFilters) => void;
  onFilteredRequestsChange?: (requests: SpotRequest[]) => void;
  isLoading: boolean;
  onAddSpot?: () => void;
}

const RequestListPanel: React.FC<RequestListPanelProps> = ({
  requests,
  selectedRequestId,
  selectedRequestType,
  hoveredRequestId,
  hoveredRequestType,
  onRequestSelect,
  onRequestHover,
  onReview,
  onUserClick,
  filters,
  onFiltersChange,
  onFilteredRequestsChange,
  isLoading,
  onAddSpot,
}) => {
  // Calculate counts for each type
  const counts = useMemo(() => {
    return {
      all: requests.length,
      merge: requests.filter((r) => r.type === "merge").length,
      add: requests.filter((r) => r.type === "add").length,
      rename: requests.filter((r) => r.type === "rename").length,
      edit: requests.filter((r) => r.type === "edit").length,
    };
  }, [requests]);

  // Filter and sort requests
  const filteredRequests = useMemo(() => {
    let result = [...requests];

    // Filter by type
    if (filters.requestType !== "all") {
      result = result.filter((r) => r.type === filters.requestType);
    }

    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter((r) => {
        if (r.type === "merge") {
          const data = r.data as MergeSpotRequest;
          return (
            data.sourcespotname.toLowerCase().includes(query) ||
            data.targetspotname.toLowerCase().includes(query) ||
            data.username?.toLowerCase().includes(query)
          );
        } else {
          const data = r.data as SpotAddRequest;
          return (
            data.spotname?.toLowerCase().includes(query) ||
            data.oldname?.toLowerCase().includes(query) ||
            data.newname?.toLowerCase().includes(query) ||
            data.username?.toLowerCase().includes(query)
          );
        }
      });
    }

    // Sort
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case "newest": {
          const aDate = a.data.createdAt ? new Date(a.data.createdAt).getTime() : 0;
          const bDate = b.data.createdAt ? new Date(b.data.createdAt).getTime() : 0;
          return bDate - aDate; // Descending (newest first)
        }
        case "oldest": {
          const aDate = a.data.createdAt ? new Date(a.data.createdAt).getTime() : 0;
          const bDate = b.data.createdAt ? new Date(b.data.createdAt).getTime() : 0;
          return aDate - bDate; // Ascending (oldest first)
        }
        case "distance":
          if (a.type === "merge" && b.type === "merge") {
            return getMergeRequestDistance(a.data as MergeSpotRequest) -
                   getMergeRequestDistance(b.data as MergeSpotRequest);
          }
          return 0;
        case "sessions":
          if (a.type === "merge" && b.type === "merge") {
            const aData = a.data as MergeSpotRequest;
            const bData = b.data as MergeSpotRequest;
            return ((bData.targetnsessions ?? 0) + (bData.sourcensessions ?? 0)) -
                   ((aData.targetnsessions ?? 0) + (aData.sourcensessions ?? 0));
          }
          return 0;
        default:
          return 0;
      }
    });

    return result;
  }, [requests, filters]);

  // Notify parent when filtered requests change (for map sync)
  useEffect(() => {
    onFilteredRequestsChange?.(filteredRequests);
  }, [filteredRequests, onFilteredRequestsChange]);

  return (
    <div className="h-full flex flex-col bg-gray-900 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-semibold">
            Pending Requests ({filteredRequests.length})
          </h2>
          {onAddSpot && (
            <button
              onClick={onAddSpot}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors"
              title="Add a new spot"
            >
              <PlusIcon className="h-4 w-4" />
              Add Spot
            </button>
          )}
        </div>
        <RequestFilters
          filters={filters}
          onFiltersChange={onFiltersChange}
          counts={counts}
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            {filters.searchQuery || filters.requestType !== "all"
              ? "No requests match your filters"
              : "No pending requests"
            }
          </div>
        ) : (
          filteredRequests.map((request) => (
            <RequestCard
              key={`${request.type}-${request.id}`}
              request={request}
              isSelected={selectedRequestId === request.id && selectedRequestType === request.type}
              isHovered={hoveredRequestId === request.id && hoveredRequestType === request.type}
              onSelect={() => onRequestSelect(request)}
              onHover={() => onRequestHover(request)}
              onHoverEnd={() => onRequestHover(null)}
              onReview={() => onReview(request)}
              onUserClick={onUserClick}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default RequestListPanel;
